import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Package,
  Eye,
  Filter,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ProductForm from "@/components/forms/ProductForm";
import { useNavigate } from "react-router-dom";

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  stock: number;
  category: string;
  image_url: string;
  is_active: boolean;
  created_at: string;
  stores?: { name: string };
}

const ProductManagement = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);

  const { user, userRole, userProfile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Filter products based on search term and filters
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = categoryFilter === "all" || product.category === categoryFilter;

    const matchesStatus = statusFilter === "all" ||
      (statusFilter === "active" && product.is_active) ||
      (statusFilter === "inactive" && !product.is_active);

    return matchesSearch && matchesCategory && matchesStatus;
  });

  const fetchProducts = async () => {
    try {
      let query = supabase
        .from('products')
        .select('*, stores(name)')
        .order('created_at', { ascending: false });

      if (userRole === 'staff_toko' && userProfile?.store_id) {
        query = query.eq('store_id', userProfile.store_id);
      }

      const { data, error } = await query;
      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Gagal memuat data produk",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && userRole) {
      fetchProducts();
    }
  }, [user, userRole]);

  const handleProductAdded = () => {
    fetchProducts();
    setEditingProduct(null);
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setShowProductForm(true);
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!confirm("Apakah Anda yakin ingin menghapus produk ini?")) return;
    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);
      if (error) throw error;
      toast({
        title: "Produk dihapus",
        description: "Produk berhasil dihapus dari katalog",
      });
      fetchProducts();
    } catch (error: any) {
      toast({
        title: "Gagal menghapus produk",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getCategoryBadge = (category: string) => {
    const variants = {
      makanan: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
      minuman: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
      merchandise: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
      kerajinan: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    };
    return variants[category as keyof typeof variants] || variants.makanan;
  };

  const openAddDialog = () => {
    setEditingProduct(null);
    setShowProductForm(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Memuat data produk...</p>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AppSidebar />
        <div className="flex-1">
          <header className="h-12 flex items-center border-b">
            <SidebarTrigger className="ml-2" />
            <h1 className="ml-4 font-semibold">Manajemen Produk</h1>
          </header>
          <main className="flex-1">
            {/* Header */}
            <div className="border-b border-border bg-gradient-card">
              <div className="container mx-auto px-4 py-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Manajemen Produk</h1>
                    <p className="text-muted-foreground">
                      Kelola katalog produk bazaar kemerdekaan
                    </p>
                  </div>
                  <Button variant="default" onClick={openAddDialog}>
                    <Plus className="w-4 h-4 mr-2" />
                    Tambah Produk
                  </Button>
                </div>
              </div>
            </div>

            <div className="container mx-auto px-4 py-8">
              {/* Search and Filters */}
              <Card className="mb-6 bg-gradient-card border-none shadow-soft">
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row gap-4">
                    {/* Search */}
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <Input
                        placeholder="Cari produk berdasarkan nama atau deskripsi..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>

                    {/* Filters */}
                    <div className="flex gap-2">
                      <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue placeholder="Kategori" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Semua Kategori</SelectItem>
                          <SelectItem value="makanan">Makanan</SelectItem>
                          <SelectItem value="minuman">Minuman</SelectItem>
                          <SelectItem value="merchandise">Merchandise</SelectItem>
                          <SelectItem value="kerajinan">Kerajinan</SelectItem>
                        </SelectContent>
                      </Select>

                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[130px]">
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Semua Status</SelectItem>
                          <SelectItem value="active">Aktif</SelectItem>
                          <SelectItem value="inactive">Non-aktif</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Products Grid */}
              <Card className="bg-gradient-card border-none shadow-soft">
                <CardHeader>
                  <CardTitle>Daftar Produk</CardTitle>
                  <CardDescription>
                    Menampilkan {filteredProducts.length} dari {products.length} produk
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredProducts.length === 0 ? (
                    <div className="text-center py-12">
                      <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {searchTerm || categoryFilter !== "all" || statusFilter !== "all"
                          ? "Tidak ada produk yang ditemukan"
                          : "Belum ada produk"
                        }
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        {searchTerm || categoryFilter !== "all" || statusFilter !== "all"
                          ? "Coba ubah filter atau kata kunci pencarian"
                          : "Mulai dengan menambahkan produk pertama Anda"
                        }
                      </p>
                      {!(searchTerm || categoryFilter !== "all" || statusFilter !== "all") && (
                        <Button variant="default" onClick={openAddDialog}>
                          <Plus className="w-4 h-4 mr-2" />
                          Tambah Produk
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredProducts.map((product) => (
                        <Card key={product.id} className="overflow-hidden hover:shadow-soft transition-all">
                          <div className="aspect-video relative">
                            {product.image_url ? (
                              <img
                                src={product.image_url}
                                alt={product.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.src = '/placeholder.svg';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full bg-muted flex items-center justify-center">
                                <Package className="w-12 h-12 text-muted-foreground" />
                              </div>
                            )}
                            <Badge
                              className={`absolute top-2 left-2 ${getCategoryBadge(product.category)}`}
                            >
                              {product.category}
                            </Badge>
                            {!product.is_active && (
                              <Badge className="absolute top-2 right-2 bg-gray-500 text-white">
                                Non-aktif
                              </Badge>
                            )}
                          </div>
                          <CardContent className="p-4">
                            <h3 className="font-semibold text-foreground mb-1 line-clamp-1">
                              {product.name}
                            </h3>
                            <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                              {product.description || "Tidak ada deskripsi"}
                            </p>
                            <p className="text-lg font-bold text-primary mb-3">
                              {formatPrice(product.price)}
                            </p>
                            {product.stores && (
                              <p className="text-xs text-muted-foreground mb-3">
                                Toko: {product.stores.name}
                              </p>
                            )}
                            <div className="flex space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex-1"
                                onClick={() => navigate(`/admin/product/${product.id}`)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Detail
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex-1"
                                onClick={() => handleEditProduct(product)}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteProduct(product.id)}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Product Form Dialog */}
            <Dialog open={showProductForm} onOpenChange={setShowProductForm}>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingProduct ? "Edit Produk" : "Tambah Produk Baru"}
                  </DialogTitle>
                </DialogHeader>
                <ProductForm
                  onProductAdded={handleProductAdded}
                  onClose={() => {
                    setShowProductForm(false);
                    setEditingProduct(null);
                  }}
                  productToEdit={editingProduct}
                />
              </DialogContent>
            </Dialog>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default ProductManagement;